import type { ColumnDef } from '@tanstack/react-table';
import type { TFunction } from 'i18next';
import type { AmountUnit } from '@/shared/lib/amount/convertAmount';
import { Text, WIDTH_PRESETS } from '@/shared/ui';
import type { DefaultAccumulationTableRowData } from './formatted-default-accumulations-table-data';

type AccumulationsTableColumnsProps = {
  /** 基準年度 */
  baseFiscalYear: number;
  /** 比較年度 */
  comparedFiscalYear: number;
  /** 金額単位 */
  amountUnit: AmountUnit;
  /** サイドパネル表示かどうか */
  isSidePanelView?: boolean;
  /** 翻訳関数 */
  t: TFunction;
};

const AMOUNT_MIN_COLUMN_WIDTH = 88;
const NAME_MIN_COLUMN_WIDTH = WIDTH_PRESETS.l / 2 - (WIDTH_PRESETS.l / 2 - AMOUNT_MIN_COLUMN_WIDTH);

/**
 * 積算明細テーブルのカラム定義を生成
 */
export const createDefaultAccumulationsTableColumns = ({
  baseFiscalYear,
  comparedFiscalYear,
  amountUnit,
  isSidePanelView,
  t,
}: AccumulationsTableColumnsProps): ColumnDef<DefaultAccumulationTableRowData>[] => {
  return [
    {
      id: 'base',
      header: t('assessment.comparison-table.accumulation-detail', { year: baseFiscalYear }),
      meta: { groupWidthWeight: 1 },
      columns: [
        {
          id: 'base_name',
          header: '',
          meta: { minWidth: NAME_MIN_COLUMN_WIDTH, columnWidthWeight: 0.5 },
          cell: ({ row }) => {
            return <Text>{row.original.base?.name}</Text>;
          },
        },
        {
          id: 'base_amount',
          header: '',
          meta: { minWidth: AMOUNT_MIN_COLUMN_WIDTH, columnWidthWeight: 0.5 },
          cell: ({ row }) => {
            return (
              <Text className="w-full text-right" viewMode="number" unit={amountUnit}>
                {row.original.base?.amount}
              </Text>
            );
          },
        },
      ],
    },
    {
      id: 'compared',
      header: t('assessment.comparison-table.accumulation-detail', { year: comparedFiscalYear }),
      meta: { groupWidthWeight: 1 },
      columns: [
        {
          id: 'compared_name',
          header: '',
          meta: { minWidth: NAME_MIN_COLUMN_WIDTH, columnWidthWeight: 0.5 },
          cell: ({ row }) => {
            return <Text>{row.original.compared?.name}</Text>;
          },
        },
        {
          id: 'compared_amount',
          header: '',
          meta: { minWidth: AMOUNT_MIN_COLUMN_WIDTH, columnWidthWeight: 0.5 },
          cell: ({ row }) => {
            return (
              <Text className="w-full text-right" viewMode="number" unit={amountUnit}>
                {row.original.compared?.amount}
              </Text>
            );
          },
        },
      ],
    },
    ...(!isSidePanelView
      ? [
          {
            id: 'empty',
            header: '',
            meta: { groupWidthWeight: 1, minWidth: 'm' as const, columnWidthWeight: 1 },
          },
        ]
      : []),
  ];
};
